import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Snackbar,
  Alert,
  Chip,
  Card,
  CardContent,
  CardActions,
  Grid,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Article as ArticleIcon,
  FileCopy as FileCopyIcon,
} from '@mui/icons-material';
import { usePages } from '../hooks/queries';
import useDialog from '../hooks/useDialog';
import useSnackbar from '../hooks/useSnackbar';

// URL base das páginas públicas - usa variável de ambiente
const PAGES_BASE_URL = import.meta.env.VITE_PAGES_URL || 'https://pages.agendario.com.br';
import LoadingState from '../components/ui/LoadingState';
import EmptyState from '../components/ui/EmptyState';
import ErrorState from '../components/ui/ErrorState';
import PageTypeSelector from '../components/pages/PageTypeSelector';
import pagesService from '../services/pagesService';
import { handleDeleteOperation } from '../utils/apiErrorHandler';

export default function PagesPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // React Query Hooks
  const { items: pages, loading, error, reload, create, update, deleteItem } = usePages();

  // Dialog States
  const typeSelector = useDialog();
  const deleteDialog = useDialog();

  // Snackbar State
  const { snackbar, showSnackbar, hideSnackbar, showSuccess, showError } = useSnackbar();

  // Handlers
  const handleCreateNew = () => {
    typeSelector.handleOpen();
  };

  const handleDelete = async () => {
    try {
      const result = await handleDeleteOperation(
        (params) => pagesService.delete(params.id),
        { id: deleteDialog.data.id }
      );

      if (result.success) {
        deleteDialog.handleClose();
        await reload();
        showSuccess('Página excluída com sucesso!');
      } else {
        const errorMsg = result.error.message;
        showError(errorMsg);
      }
    } catch (err) {
      showError('Erro ao excluir página: ' + (err.response?.data?.message || err.message));
    }
  };

  const handlePreview = (page) => {
    // Abrir em nova aba a página pública
    window.open(`/pages/${page.shortId}`, '_blank');
  };

  const handleCopyUrl = (page) => {
    const url = `${PAGES_BASE_URL}/${page.slug}-${page.shortId}`;
    navigator.clipboard.writeText(url).then(() => {
      showSuccess('URL copiada para a área de transferência!');
    }).catch(() => {
      showError('Erro ao copiar URL');
    });
  };

  // Loading/Error States
  if (loading) return <LoadingState message="Carregando páginas..." />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Páginas e Links
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gerencie suas páginas personalizadas e publique links em bio
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateNew}
        >
          Nova Página
        </Button>
      </Box>

      {/* Empty State */}
      {pages.length === 0 ? (
        <EmptyState
          icon={ArticleIcon}
          title="Nenhuma página cadastrada"
          message="Clique em 'Nova Página' para criar sua primeira página Link-in-Bio."
          action={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateNew}
            >
              Nova Página
            </Button>
          }
        />
      ) : isMobile ? (
        // MOBILE: Cards Grid
        <Grid container spacing={2}>
          {pages.map((page) => (
            <Grid item xs={12} key={page.id}>
              <Card elevation={2}>
                <CardContent sx={{ pb: 1 }}>
                  <Box sx={{ mb: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                      <Typography variant="h6" fontWeight="bold">
                        {page.title}
                      </Typography>
                      {page.isPublished && (
                        <Chip label="Publicada" size="small" color="success" variant="outlined" />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box
                        component="a"
                        href={`${PAGES_BASE_URL}/${page.slug}-${page.shortId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.8rem',
                          color: '#1976d2',
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {PAGES_BASE_URL}/{page.slug}-{page.shortId}
                      </Box>
                      <Tooltip title="Copiar URL">
                        <IconButton
                          size="small"
                          onClick={() => handleCopyUrl(page)}
                          sx={{ p: 0 }}
                        >
                          <FileCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 1.5 }}>
                    <Chip
                      label={page.type}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                </CardContent>

                <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2, flexWrap: 'wrap', gap: 1 }}>
                  {page.isPublished && (
                    <Tooltip title="Preview">
                      <IconButton
                        size="small"
                        onClick={() => handlePreview(page)}
                        color="primary"
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Editar">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/pages/edit/${page.id}`)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <IconButton
                      size="small"
                      onClick={() => deleteDialog.handleOpen(page)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        // DESKTOP: Table
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Título</strong></TableCell>
                <TableCell><strong>URL Pública</strong></TableCell>
                <TableCell><strong>Tipo</strong></TableCell>
                <TableCell align="right"><strong>Ações</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pages.map((page) => (
                <TableRow key={page.id}>
                  <TableCell>
                    <Typography variant="body1" fontWeight="medium">
                      {page.title}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        component="a"
                        href={`${PAGES_BASE_URL}/${page.slug}-${page.shortId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.85rem',
                          color: '#1976d2',
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {PAGES_BASE_URL}/{page.slug}-{page.shortId}
                      </Box>
                      <Tooltip title="Copiar URL completa">
                        <IconButton
                          size="small"
                          onClick={() => handleCopyUrl(page)}
                          sx={{ p: 0.5 }}
                        >
                          <FileCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={page.type} size="small" />
                  </TableCell>
                  <TableCell align="right">
                    {page.isPublished && (
                      <Tooltip title="Preview">
                        <IconButton
                          size="small"
                          onClick={() => handlePreview(page)}
                          color="primary"
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/pages/edit/${page.id}`)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton
                        size="small"
                        onClick={() => deleteDialog.handleOpen(page)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Page Type Selector Modal */}
      <PageTypeSelector
        open={typeSelector.open}
        onClose={typeSelector.handleClose}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={deleteDialog.handleClose}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Atenção! Ao excluir esta página, ela será permanentemente removida (soft delete).
          </Alert>
          <Typography>
            Tem certeza que deseja excluir a página <strong>{deleteDialog.data?.title}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={deleteDialog.handleClose}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={hideSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
